export interface Integration {
  id: string;
  name: string;
  description: string;
  category: string;
  logo: string;
  status: 'available' | 'coming-soon';
  featured?: boolean;
  aiEnhanced?: boolean;
  color?: string;
}

export const integrationCategories = [
  { id: 'all', name: 'All Integrations', icon: 'ri-apps-line' },
  { id: 'social', name: 'Social Media', icon: 'ri-share-line' },
  { id: 'ai-content', name: 'AI & Content', icon: 'ri-ai-generate' },
  { id: 'automation', name: 'Automation', icon: 'ri-flashlight-line' },
  { id: 'productivity', name: 'Productivity', icon: 'ri-briefcase-line' },
  { id: 'crm', name: 'CRM & Sales', icon: 'ri-customer-service-line' },
  { id: 'ecommerce', name: 'E-commerce', icon: 'ri-shopping-cart-line' },
  { id: 'storage', name: 'Storage & Media', icon: 'ri-folder-cloud-line' },
  { id: 'specialized', name: 'Specialized', icon: 'ri-star-line' },
];

export const integrations: Integration[] = [
  // Social Media Platforms
  {
    id: 'facebook',
    name: 'Facebook',
    description: 'Share posts, stories, and reels to Pages and Groups',
    category: 'social',
    logo: 'ri-facebook-fill',
    status: 'available',
    featured: true,
    aiEnhanced: true,
    color: '#1877F2',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    description: 'Post feed content, stories, and reels automatically',
    category: 'social',
    logo: 'ri-instagram-line',
    status: 'available',
    featured: true,
    aiEnhanced: true,
    color: '#E4405F',
  },
  {
    id: 'twitter',
    name: 'Twitter / X',
    description: 'Schedule tweets and threads with optimal timing',
    category: 'social',
    logo: 'ri-twitter-x-line',
    status: 'available',
    aiEnhanced: true,
    color: '#000000',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    description: 'Publish to company pages and personal profiles',
    category: 'social',
    logo: 'ri-linkedin-fill',
    status: 'available',
    featured: true,
    aiEnhanced: true,
    color: '#0A66C2',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    description: 'Schedule and publish short-form video content',
    category: 'social',
    logo: 'ri-tiktok-line',
    status: 'available',
    aiEnhanced: true,
    color: '#000000',
  },
  {
    id: 'pinterest',
    name: 'Pinterest',
    description: 'Create and schedule pins to boards automatically',
    category: 'social',
    logo: 'ri-pinterest-fill',
    status: 'available',
    color: '#E60023',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    description: 'Upload videos and manage your channel presence',
    category: 'social',
    logo: 'ri-youtube-fill',
    status: 'available',
    aiEnhanced: true,
    color: '#FF0000',
  },
  {
    id: 'threads',
    name: 'Threads',
    description: 'Share text updates on Meta\'s newest platform',
    category: 'social',
    logo: 'ri-at-line',
    status: 'available',
    color: '#000000',
  },
  {
    id: 'bluesky',
    name: 'Bluesky',
    description: 'Post to the decentralized social network',
    category: 'social',
    logo: 'ri-cloud-line',
    status: 'available',
    color: '#0085FF',
  },
  {
    id: 'google-business',
    name: 'Google Business',
    description: 'Manage your Google Business Profile posts',
    category: 'social',
    logo: 'ri-google-fill',
    status: 'available',
    color: '#4285F4',
  },
  {
    id: 'mastodon',
    name: 'Mastodon',
    description: 'Share to the federated social network',
    category: 'social',
    logo: 'ri-mastodon-line',
    status: 'coming-soon',
    color: '#6364FF',
  },
  {
    id: 'reddit',
    name: 'Reddit',
    description: 'Post to subreddits and manage community engagement',
    category: 'social',
    logo: 'ri-reddit-fill',
    status: 'coming-soon',
    color: '#FF4500',
  },

  // AI & Content Creation
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'AI-powered content generation with ChatGPT',
    category: 'ai-content',
    logo: 'ri-ai-generate',
    status: 'available',
    featured: true,
    aiEnhanced: true,
    color: '#10A37F',
  },
  {
    id: 'canva',
    name: 'Canva',
    description: 'Create stunning graphics directly in your workflow',
    category: 'ai-content',
    logo: 'ri-palette-line',
    status: 'available',
    featured: true,
    color: '#00C4CC',
  },
  {
    id: 'unsplash',
    name: 'Unsplash',
    description: 'Access millions of free high-quality stock photos',
    category: 'ai-content',
    logo: 'ri-image-line',
    status: 'available',
    color: '#000000',
  },
  {
    id: 'giphy',
    name: 'Giphy',
    description: 'Add GIFs and animations to your content',
    category: 'ai-content',
    logo: 'ri-emotion-laugh-line',
    status: 'available',
    color: '#00FF99',
  },
  {
    id: 'pexels',
    name: 'Pexels',
    description: 'Browse and use free stock photos and videos',
    category: 'ai-content',
    logo: 'ri-camera-line',
    status: 'available',
    color: '#05A081',
  },
  {
    id: 'adobe-express',
    name: 'Adobe Express',
    description: 'Professional design tools integrated seamlessly',
    category: 'ai-content',
    logo: 'ri-edit-box-line',
    status: 'coming-soon',
    color: '#FF0000',
  },

  // Automation & Integration
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Connect with 5,000+ apps through automation',
    category: 'automation',
    logo: 'ri-flashlight-line',
    status: 'available',
    featured: true,
    color: '#FF4A00',
  },
  {
    id: 'make',
    name: 'Make',
    description: 'Advanced workflow automation and integration',
    category: 'automation',
    logo: 'ri-git-merge-line',
    status: 'available',
    color: '#6B47ED',
  },
  {
    id: 'api',
    name: 'REST API',
    description: 'Build custom integrations with our public API',
    category: 'automation',
    logo: 'ri-code-box-line',
    status: 'available',
    color: '#22577A',
  },
  {
    id: 'webhooks',
    name: 'Webhooks',
    description: 'Real-time event notifications for your workflows',
    category: 'automation',
    logo: 'ri-webhook-line',
    status: 'available',
    color: '#38a3a5',
  },

  // Productivity & Collaboration
  {
    id: 'google-workspace',
    name: 'Google Workspace',
    description: 'Sync with Calendar, Drive, and Sheets',
    category: 'productivity',
    logo: 'ri-google-fill',
    status: 'available',
    color: '#4285F4',
  },
  {
    id: 'microsoft-365',
    name: 'Microsoft 365',
    description: 'Integrate with Teams, Outlook, and OneDrive',
    category: 'productivity',
    logo: 'ri-microsoft-fill',
    status: 'available',
    color: '#0078D4',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Get notifications and manage content from Slack',
    category: 'productivity',
    logo: 'ri-slack-fill',
    status: 'available',
    color: '#4A154B',
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    description: 'Collaborate and approve content in Teams',
    category: 'productivity',
    logo: 'ri-team-line',
    status: 'available',
    color: '#6264A7',
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'Sync content ideas from your Notion workspace',
    category: 'productivity',
    logo: 'ri-notion-fill',
    status: 'coming-soon',
    color: '#000000',
  },
  {
    id: 'monday',
    name: 'Monday.com',
    description: 'Manage campaigns with Monday.com boards',
    category: 'productivity',
    logo: 'ri-calendar-check-line',
    status: 'coming-soon',
    color: '#FF3D57',
  },
  {
    id: 'asana',
    name: 'Asana',
    description: 'Track marketing projects in Asana',
    category: 'productivity',
    logo: 'ri-task-line',
    status: 'coming-soon',
    color: '#F06A6A',
  },
  {
    id: 'clickup',
    name: 'ClickUp',
    description: 'Integrate with your ClickUp workflows',
    category: 'productivity',
    logo: 'ri-mouse-line',
    status: 'coming-soon',
    color: '#7B68EE',
  },

  // CRM & Sales
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Sync contacts and track marketing campaigns',
    category: 'crm',
    logo: 'ri-hub-line',
    status: 'available',
    color: '#FF7A59',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Connect with the world\'s #1 CRM platform',
    category: 'crm',
    logo: 'ri-cloud-fill',
    status: 'available',
    color: '#00A1E0',
  },
  {
    id: 'zendesk',
    name: 'Zendesk',
    description: 'Manage customer support and engagement',
    category: 'crm',
    logo: 'ri-customer-service-2-line',
    status: 'coming-soon',
    color: '#03363D',
  },
  {
    id: 'dynamics',
    name: 'Microsoft Dynamics',
    description: 'Enterprise CRM integration',
    category: 'crm',
    logo: 'ri-building-line',
    status: 'coming-soon',
    color: '#002050',
  },

  // E-commerce & Marketing
  {
    id: 'shopify',
    name: 'Shopify',
    description: 'Promote products and sync your store catalog',
    category: 'ecommerce',
    logo: 'ri-shopping-bag-line',
    status: 'available',
    color: '#96BF48',
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    description: 'WordPress e-commerce integration',
    category: 'ecommerce',
    logo: 'ri-store-line',
    status: 'coming-soon',
    color: '#96588A',
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Email marketing and audience management',
    category: 'ecommerce',
    logo: 'ri-mail-send-line',
    status: 'available',
    color: '#FFE01B',
  },
  {
    id: 'activecampaign',
    name: 'ActiveCampaign',
    description: 'Marketing automation and email campaigns',
    category: 'ecommerce',
    logo: 'ri-mail-line',
    status: 'coming-soon',
    color: '#356AE6',
  },
  {
    id: 'wordpress',
    name: 'WordPress',
    description: 'Publish blog posts and manage content',
    category: 'ecommerce',
    logo: 'ri-wordpress-line',
    status: 'available',
    color: '#21759B',
  },
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    description: 'Track campaign performance and ROI',
    category: 'ecommerce',
    logo: 'ri-line-chart-line',
    status: 'available',
    color: '#F9AB00',
  },

  // Storage & Media
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Store and access your content files',
    category: 'storage',
    logo: 'ri-google-fill',
    status: 'available',
    color: '#4285F4',
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    description: 'Sync files from your Dropbox account',
    category: 'storage',
    logo: 'ri-dropbox-fill',
    status: 'available',
    color: '#0061FF',
  },
  {
    id: 'onedrive',
    name: 'OneDrive',
    description: 'Microsoft cloud storage integration',
    category: 'storage',
    logo: 'ri-cloud-line',
    status: 'available',
    color: '#0078D4',
  },
  {
    id: 'bitly',
    name: 'Bit.ly',
    description: 'Shorten and track your marketing links',
    category: 'storage',
    logo: 'ri-link',
    status: 'available',
    color: '#EE6123',
  },

  // Specialized Tools
  {
    id: 'rss',
    name: 'RSS Feeds',
    description: 'Auto-curate content from RSS feeds with AI',
    category: 'specialized',
    logo: 'ri-rss-line',
    status: 'available',
    aiEnhanced: true,
    color: '#FFA500',
  },
  {
    id: 'reviews',
    name: 'Review Management',
    description: 'Monitor and respond to reviews with AI',
    category: 'specialized',
    logo: 'ri-star-line',
    status: 'coming-soon',
    aiEnhanced: true,
    color: '#FFD700',
  },
  {
    id: 'vimeo',
    name: 'Vimeo',
    description: 'Professional video hosting and sharing',
    category: 'specialized',
    logo: 'ri-video-line',
    status: 'coming-soon',
    color: '#1AB7EA',
  },
  {
    id: 'webflow',
    name: 'Webflow',
    description: 'Publish blog posts to your Webflow site',
    category: 'specialized',
    logo: 'ri-layout-line',
    status: 'coming-soon',
    color: '#4353FF',
  },
  {
    id: 'medium',
    name: 'Medium',
    description: 'Cross-post articles to Medium',
    category: 'specialized',
    logo: 'ri-medium-fill',
    status: 'coming-soon',
    color: '#000000',
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    description: 'Manage customer conversations at scale',
    category: 'specialized',
    logo: 'ri-whatsapp-line',
    status: 'coming-soon',
    color: '#25D366',
  },
];
